import { FC } from 'react'
import Select, { components, SingleValueProps, OptionProps } from 'react-select'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import {
  getConnectedPlatformCountryCode,
  getConnectedPlatformLabel,
} from '../utils/connectedPlatformLabel'
import ListingProfilePlatformLogo from './ListingProfilePlatformLogo'

export type TargetChannelOption = {
  value: number
  platform: PlatformDataType
  label: string
}

const channelOptionSearchText = (platform: PlatformDataType) => {
  const label = getConnectedPlatformLabel(platform)
  const countryCode = getConnectedPlatformCountryCode(platform)
  return [label, countryCode, platform.display_name, platform.platform_info?.name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

const ChannelOptionVisual: FC<{ platform: PlatformDataType; showLabel?: boolean }> = ({
  platform,
  showLabel = true,
}) => {
  const label = getConnectedPlatformLabel(platform)
  const countryCode = getConnectedPlatformCountryCode(platform)

  return (
    <span className="product-listing__target-channel-option">
      <ListingProfilePlatformLogo platform={platform} />
      {showLabel ? (
        <span className="product-listing__target-channel-option-copy">
          {countryCode ? (
            <span className="product-listing__target-channel-option-country">{countryCode}</span>
          ) : null}
          <span className="product-listing__target-channel-option-name">{label}</span>
        </span>
      ) : null}
    </span>
  )
}

const Option = (props: OptionProps<TargetChannelOption>) => (
  <components.Option {...props}>
    <ChannelOptionVisual platform={props.data.platform} />
  </components.Option>
)

const SingleValue = (props: SingleValueProps<TargetChannelOption>) => (
  <components.SingleValue {...props}>
    <ChannelOptionVisual platform={props.data.platform} />
  </components.SingleValue>
)

interface ListingProfileTargetChannelSelectProps {
  platforms: PlatformDataType[]
  valuePlatformId: number | null
  onChange: (platformId: number) => void
  disabled?: boolean
}

const ListingProfileTargetChannelSelect: FC<ListingProfileTargetChannelSelectProps> = ({
  platforms,
  valuePlatformId,
  onChange,
  disabled = false,
}) => {
  const options: TargetChannelOption[] = platforms
    .filter((p) => p.id != null)
    .map((platform) => ({
      value: platform.id!,
      platform,
      label: getConnectedPlatformLabel(platform),
    }))

  const selected = options.find((o) => o.value === valuePlatformId) ?? null

  return (
    <Select<TargetChannelOption>
      classNamePrefix="listing-target-channel"
      options={options}
      value={selected}
      placeholder="Type to search channel..."
      isDisabled={disabled || options.length === 0}
      isSearchable
      getOptionLabel={(option) => option.label}
      filterOption={(option, input) =>
        channelOptionSearchText(option.data.platform).includes(input.trim().toLowerCase())
      }
      components={{ Option, SingleValue }}
      onChange={(opt) => {
        if (opt?.value != null) onChange(opt.value)
      }}
    />
  )
}

export default ListingProfileTargetChannelSelect

const ALL_CHANNELS = '__all_channels__' as const

type ChannelFilterOption =
  | { value: typeof ALL_CHANNELS; platform?: undefined; label: string }
  | { value: number; platform: PlatformDataType; label: string }

const FilterOptionRow = (props: OptionProps<ChannelFilterOption>) => {
  if (props.data.value === ALL_CHANNELS) {
    return (
      <components.Option {...props}>
        <span className="product-listing__channel-filter-all-label">All channels</span>
      </components.Option>
    )
  }
  return (
    <components.Option {...props}>
      <ChannelOptionVisual platform={props.data.platform} />
    </components.Option>
  )
}

const FilterSingleValue = (props: SingleValueProps<ChannelFilterOption>) => {
  if (props.data.value === ALL_CHANNELS) {
    return (
      <components.SingleValue {...props}>
        <span className="product-listing__channel-filter-all-label">All channels</span>
      </components.SingleValue>
    )
  }
  return (
    <components.SingleValue {...props}>
      <ChannelOptionVisual platform={props.data.platform} />
    </components.SingleValue>
  )
}

interface ListingProfileChannelFilterSelectProps {
  platforms: PlatformDataType[]
  valuePlatformId: number | null
  onChange: (platformId: number | null) => void
  disabled?: boolean
}

export const ListingProfileChannelFilterSelect: FC<ListingProfileChannelFilterSelectProps> = ({
  platforms,
  valuePlatformId,
  onChange,
  disabled = false,
}) => {
  const platformOptions: ChannelFilterOption[] = platforms
    .filter((p) => p.id != null)
    .map((platform) => ({
      value: platform.id!,
      platform,
      label: getConnectedPlatformLabel(platform),
    }))

  const options: ChannelFilterOption[] = [
    { value: ALL_CHANNELS, label: 'All channels' },
    ...platformOptions,
  ]

  const selected =
    valuePlatformId == null
      ? options[0]
      : platformOptions.find((o) => o.value === valuePlatformId) ?? options[0]

  const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined

  return (
    <Select<ChannelFilterOption>
      classNamePrefix="listing-target-channel"
      inputId="listing-profile-channel-filter"
      aria-label="Filter by channel"
      options={options}
      value={selected}
      isDisabled={disabled}
      isSearchable
      getOptionLabel={(option) => option.label}
      filterOption={(option, input) => {
        const query = input.trim().toLowerCase()
        if (!query) return true
        if (option.data.value === ALL_CHANNELS) {
          return 'all channels'.includes(query)
        }
        return channelOptionSearchText(option.data.platform).includes(query)
      }}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 1060 }),
      }}
      components={{ Option: FilterOptionRow, SingleValue: FilterSingleValue }}
      onChange={(opt) => {
        if (!opt || opt.value === ALL_CHANNELS) {
          onChange(null)
          return
        }
        onChange(opt.value)
      }}
    />
  )
}
